// Vanilla SVG renderer for AxesSubstrate — ported from
// thoughtful-oasis-v8.2/substrate-axes.js. No React, no Babel. Mounts an
// animated iso wireframe with named axes into a container element.

const SVG_NS = 'http://www.w3.org/2000/svg';

const COS30 = Math.cos(Math.PI / 6);
const SIN30 = Math.sin(Math.PI / 6);

function project(x: number, y: number, z: number, rotY: number): [number, number] {
  const cr = Math.cos(rotY);
  const sr = Math.sin(rotY);
  const xr = x * cr - z * sr;
  const zr = x * sr + z * cr;
  return [(xr - zr) * COS30, -y + (xr + zr) * SIN30];
}
function pt(x: number, y: number, z: number, rotY: number): string {
  const p = project(x, y, z, rotY);
  return p[0].toFixed(2) + ',' + p[1].toFixed(2);
}

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const S = 130;
const SCATTER = (() => {
  const rng = mulberry32(7);
  const pts: { x: number; y: number; z: number; size: number }[] = [];
  for (let i = 0; i < 64; i++) {
    pts.push({
      x: (rng() * 2 - 1) * (S - 18),
      y: (rng() * 2 - 1) * (S - 18),
      z: (rng() * 2 - 1) * (S - 18),
      size: 1.6 + rng() * 1.6,
    });
  }
  return pts;
})();
const HEROES = [
  { x: 60, y: 40, z: -50, label: 'cmpd_A471', tag: 'candidate' },
  { x: -70, y: -20, z: 60, label: 'cmpd_B102', tag: 'reference' },
  { x: 20, y: 90, z: 20, label: 'cmpd_C913', tag: 'novel' },
];
const CORNERS: Record<string, [number, number, number]> = {
  nbl: [-S, -S, -S], nbr: [S, -S, -S], nfr: [S, -S, S], nfl: [-S, -S, S],
  tbl: [-S, S, -S], tbr: [S, S, -S], tfr: [S, S, S], tfl: [-S, S, S],
};

function renderAxis(
  from: [number, number, number],
  to: [number, number, number],
  rotY: number,
  label: string,
  tickCount: number,
  vertical: boolean
): string {
  const a = project(from[0], from[1], from[2], rotY);
  const b = project(to[0], to[1], to[2], rotY);
  const dx = (to[0] - from[0]) / tickCount;
  const dy = (to[1] - from[1]) / tickCount;
  const dz = (to[2] - from[2]) / tickCount;
  let out =
    '<line x1="' + a[0].toFixed(2) + '" y1="' + a[1].toFixed(2) +
    '" x2="' + b[0].toFixed(2) + '" y2="' + b[1].toFixed(2) +
    '" stroke="var(--ink)" stroke-width="0.9"/>';
  for (let i = 0; i <= tickCount; i++) {
    const t = project(from[0] + dx * i, from[1] + dy * i, from[2] + dz * i, rotY);
    out +=
      '<circle cx="' + t[0].toFixed(2) + '" cy="' + t[1].toFixed(2) +
      '" r="0.9" fill="var(--ink)" opacity="0.4"/>';
  }
  const lx = b[0] + (vertical ? -8 : b[0] > a[0] ? 6 : -6);
  const ly = b[1] + (vertical ? 0 : b[1] > a[1] ? 12 : -8);
  const anchor = vertical ? 'end' : b[0] > a[0] ? 'start' : 'end';
  out +=
    '<text class="axis-label" x="' + lx.toFixed(2) + '" y="' + ly.toFixed(2) +
    '" text-anchor="' + anchor + '">' + label + '</text>';
  return out;
}

function buildSubstrate(rotY: number): string {
  const r = rotY;
  const P = (k: string) => pt(CORNERS[k][0], CORNERS[k][1], CORNERS[k][2], r);
  let out = '';

  // Floor grid
  const div = 8;
  const step = (S * 2) / div;
  for (let i = 0; i <= div; i++) {
    const tt = -S + i * step;
    const ga = project(-S, -S, tt, r);
    const gb = project(S, -S, tt, r);
    out +=
      '<line x1="' + ga[0].toFixed(2) + '" y1="' + ga[1].toFixed(2) +
      '" x2="' + gb[0].toFixed(2) + '" y2="' + gb[1].toFixed(2) +
      '" stroke="var(--line)" stroke-width="0.4"/>';
    const gc = project(tt, -S, -S, r);
    const gd = project(tt, -S, S, r);
    out +=
      '<line x1="' + gc[0].toFixed(2) + '" y1="' + gc[1].toFixed(2) +
      '" x2="' + gd[0].toFixed(2) + '" y2="' + gd[1].toFixed(2) +
      '" stroke="var(--line)" stroke-width="0.4"/>';
  }

  // Back faces
  out +=
    '<polygon points="' + P('nbl') + ' ' + P('nbr') + ' ' + P('tbr') + ' ' + P('tbl') +
    '" fill="none" stroke="var(--line-2)" stroke-width="0.6"/>' +
    '<polygon points="' + P('nbr') + ' ' + P('nfr') + ' ' + P('tfr') + ' ' + P('tbr') +
    '" fill="none" stroke="var(--line-2)" stroke-width="0.6"/>' +
    '<polygon points="' + P('nbl') + ' ' + P('nfl') + ' ' + P('tfl') + ' ' + P('tbl') +
    '" fill="none" stroke="var(--line-2)" stroke-width="0.6"/>';

  // Three named axes
  out += renderAxis([-S, -S, -S], [S, -S, -S], r, 'efficacy', 5, false);
  out += renderAxis([-S, -S, -S], [-S, -S, S], r, 'toxicity', 5, false);
  out += renderAxis([-S, -S, -S], [-S, S, -S], r, 'stability', 5, true);

  // Scatter
  for (let s = 0; s < SCATTER.length; s++) {
    const sp = SCATTER[s];
    const top = project(sp.x, sp.y, sp.z, r);
    const flr = project(sp.x, -S, sp.z, r);
    out +=
      '<line x1="' + top[0].toFixed(2) + '" y1="' + top[1].toFixed(2) +
      '" x2="' + flr[0].toFixed(2) + '" y2="' + flr[1].toFixed(2) +
      '" stroke="var(--line)" stroke-width="0.4" stroke-dasharray="1.5 2"/>' +
      '<circle cx="' + flr[0].toFixed(2) + '" cy="' + flr[1].toFixed(2) +
      '" r="' + (sp.size * 0.5).toFixed(2) + '" fill="var(--ink)" opacity="0.08"/>' +
      '<circle cx="' + top[0].toFixed(2) + '" cy="' + top[1].toFixed(2) +
      '" r="' + sp.size.toFixed(2) + '" fill="var(--ink-2)" opacity="0.55"/>';
  }

  // Hero-path: B102 → C913 decomposed along three axes
  const A = HEROES[1];
  const C = HEROES[2];
  const legs: { from: [number, number, number]; to: [number, number, number]; axis: string }[] = [
    { from: [A.x, A.y, A.z], to: [C.x, A.y, A.z], axis: 'Δ efficacy' },
    { from: [C.x, A.y, A.z], to: [C.x, C.y, A.z], axis: 'Δ stability' },
    { from: [C.x, C.y, A.z], to: [C.x, C.y, C.z], axis: 'Δ toxicity' },
  ];
  for (let L = 0; L < legs.length; L++) {
    const leg = legs[L];
    const la = project(leg.from[0], leg.from[1], leg.from[2], r);
    const lb = project(leg.to[0], leg.to[1], leg.to[2], r);
    const ang = (Math.atan2(lb[1] - la[1], lb[0] - la[0]) * 180) / Math.PI;
    const mx = (la[0] + lb[0]) / 2;
    const my = (la[1] + lb[1]) / 2;
    out +=
      '<line x1="' + la[0].toFixed(2) + '" y1="' + la[1].toFixed(2) +
      '" x2="' + lb[0].toFixed(2) + '" y2="' + lb[1].toFixed(2) +
      '" stroke="var(--accent)" stroke-width="1.4" stroke-linecap="round" opacity="0.9"/>' +
      '<circle cx="' + lb[0].toFixed(2) + '" cy="' + lb[1].toFixed(2) +
      '" r="2" fill="var(--paper)" stroke="var(--accent)" stroke-width="1"/>' +
      '<text class="entity-tag" x="' + (mx + 4).toFixed(2) + '" y="' + (my - 4).toFixed(2) +
      '" style="fill: var(--accent)">' + leg.axis + '</text>';
    if (L === legs.length - 1) {
      out +=
        '<g transform="translate(' + lb[0].toFixed(2) + ',' + lb[1].toFixed(2) +
        ') rotate(' + ang.toFixed(2) + ')">' +
        '<path d="M0 0 L-7 -3 L-7 3 Z" fill="var(--accent)"/>' +
        '</g>';
    }
  }

  // Heroes
  for (let h = 0; h < HEROES.length; h++) {
    const hero = HEROES[h];
    const ha = project(hero.x, hero.y, hero.z, r);
    out +=
      '<circle cx="' + ha[0].toFixed(2) + '" cy="' + ha[1].toFixed(2) +
      '" r="4.5" fill="var(--accent)" stroke="var(--paper)" stroke-width="1.5"/>' +
      '<circle cx="' + ha[0].toFixed(2) + '" cy="' + ha[1].toFixed(2) +
      '" r="9" fill="none" stroke="var(--accent)" stroke-width="0.6" opacity="0.4"/>' +
      '<g transform="translate(' + (ha[0] + 8).toFixed(2) + ',' + (ha[1] - 8).toFixed(2) + ')">' +
      '<text class="entity-label" x="0" y="0">' + hero.label + '</text>' +
      '<text class="entity-tag" x="0" y="11">' + hero.tag + '</text>' +
      '</g>';
  }

  // Front + vertical edges (drawn last so they sit on top)
  out +=
    '<polyline points="' + P('nfl') + ' ' + P('nfr') + ' ' + P('nbr') +
    '" fill="none" stroke="var(--ink)" stroke-width="0.9"/>' +
    '<polyline points="' + P('tfl') + ' ' + P('tfr') + ' ' + P('tbr') +
    '" fill="none" stroke="var(--ink)" stroke-width="0.9" opacity="0.35"/>';
  const vertX = [-S, S, S];
  const vertZ = [S, S, -S];
  for (let v = 0; v < 3; v++) {
    const va = project(vertX[v], -S, vertZ[v], r);
    const vb = project(vertX[v], S, vertZ[v], r);
    out +=
      '<line x1="' + va[0].toFixed(2) + '" y1="' + va[1].toFixed(2) +
      '" x2="' + vb[0].toFixed(2) + '" y2="' + vb[1].toFixed(2) +
      '" stroke="var(--ink)" stroke-width="0.9" opacity="0.35"/>';
  }

  // Origin marker
  const o = project(-S, -S, -S, r);
  out +=
    '<g transform="translate(' + o[0].toFixed(2) + ',' + o[1].toFixed(2) + ')">' +
    '<circle cx="0" cy="0" r="2" fill="var(--ink)"/>' +
    '<text class="axis-label" x="-6" y="4" text-anchor="end">0</text>' +
    '</g>';

  return out;
}

export interface SubstrateOptions {
  sweepDeg?: number;
  periodSec?: number;
  animate?: boolean;
  viewBoxRadius?: number;
  background?: string;
}

export interface SubstrateHandle {
  stop: () => void;
}

export function mountAxesSubstrate(
  target: string | HTMLElement,
  opts: SubstrateOptions = {}
): SubstrateHandle | null {
  const sweepDeg = opts.sweepDeg != null ? opts.sweepDeg : 90;
  const periodSec = opts.periodSec != null ? opts.periodSec : 24;
  const animate = opts.animate != null ? opts.animate : true;
  const radius = opts.viewBoxRadius != null ? opts.viewBoxRadius : 260;
  const background = opts.background != null ? opts.background : 'var(--paper-2)';
  const sweepRad = (sweepDeg * Math.PI) / 180;

  const el = typeof target === 'string' ? document.getElementById(target) : target;
  if (!el) return null;

  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', `${-radius} ${-radius} ${radius * 2} ${radius * 2}`);
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  svg.style.width = '100%';
  svg.style.height = '100%';
  svg.style.display = 'block';

  if (background !== 'none' && background !== 'transparent') {
    const bg = document.createElementNS(SVG_NS, 'rect');
    bg.setAttribute('x', String(-radius));
    bg.setAttribute('y', String(-radius));
    bg.setAttribute('width', String(radius * 2));
    bg.setAttribute('height', String(radius * 2));
    bg.setAttribute('fill', background);
    svg.appendChild(bg);
  }

  const g = document.createElementNS(SVG_NS, 'g');
  g.setAttribute('transform', 'translate(0, -6)');
  svg.appendChild(g);

  const prior = el.querySelector(':scope > svg');
  if (prior) prior.parentNode?.removeChild(prior);
  el.insertBefore(svg, el.firstChild);

  g.innerHTML = buildSubstrate(0);

  const reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!animate || reduceMotion) {
    return { stop: () => { /* noop */ } };
  }

  const FRAME_MIN_MS = 33;
  let phase = 0;
  let lastTime = performance.now();
  let lastRender = 0;
  let raf: number | null = null;
  let visible = !document.hidden;
  let onScreen = true;

  function tick(now: number) {
    const dt = (now - lastTime) / 1000;
    lastTime = now;
    if (visible && onScreen) {
      phase = (phase + dt / periodSec) % 1;
      if (now - lastRender >= FRAME_MIN_MS) {
        lastRender = now;
        const sweep = (1 - Math.cos(2 * Math.PI * phase)) / 2;
        g.innerHTML = buildSubstrate(sweepRad * sweep);
      }
    }
    raf = requestAnimationFrame(tick);
  }
  raf = requestAnimationFrame(tick);

  function onVis() {
    visible = !document.hidden;
    lastTime = performance.now();
  }
  document.addEventListener('visibilitychange', onVis);

  let io: IntersectionObserver | null = null;
  if ('IntersectionObserver' in window) {
    io = new IntersectionObserver(
      (entries) => {
        for (let i = 0; i < entries.length; i++) {
          onScreen = entries[i].isIntersecting;
        }
        lastTime = performance.now();
      },
      { threshold: 0 }
    );
    io.observe(el);
  }

  return {
    stop: () => {
      if (raf != null) cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', onVis);
      if (io) io.disconnect();
    },
  };
}
